import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh:  <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    history:  <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 9 15"/><path d="M2.5 8.5A10 10 0 0 1 12 2"/></>,
    file:     <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    slash:    <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>,
    link:     <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
    bar:      <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    globe:    <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    phone:    <><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></>,
    monitor:  <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    map:      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    mail:     <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    search:   <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    chevron:  <><polyline points="9 18 15 12 9 6"/></>,
    info:     <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    x:        <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

const EVT = {
  open:                { label: 'Ouverture',      icon: 'file',     color: '#4db8ff', border: '#4db8ff' },
  download:            { label: 'Téléchargement', icon: 'download', color: '#ef4444', border: '#ef4444' },
  unauthorized_access: { label: 'Accès refusé',   icon: 'slash',    color: '#ef4444', border: '#ef4444' },
  link_shared:         { label: 'Lien partagé',   icon: 'link',     color: '#f59e0b', border: '#f59e0b' },
  view_progress:       { label: 'Progression',    icon: 'bar',      color: '#10b981', border: '#10b981' },
};

const FILTER_KEYS = ['', 'open', 'download', 'unauthorized_access', 'link_shared', 'view_progress'];

export default function EventsPage() {
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterType, setFilterType] = useState('');
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (filterType) params.set('eventType', filterType);
      const { data } = await api.get(`/track/events?${params}`);
      setEvents(Array.isArray(data) ? data : []);
      setLastUpdate(new Date());
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filtered = events.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (e.member_name || '').toLowerCase().includes(s) ||
      (e.member_email || '').toLowerCase().includes(s) ||
      (e.ip || '').includes(s) ||
      (e.browser || '').toLowerCase().includes(s)
    );
  });

  // Grouper par date
  const grouped = {};
  filtered.forEach(e => {
    const day = format(new Date(e.created_at), 'd MMMM yyyy', { locale: fr });
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(e);
  });

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Breadcrumb + titre */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          Administration / Historique
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#ffffff', letterSpacing: -1, marginBottom: 4 }}>
          Historique <span style={{ color: '#d4f535' }}></span>
        </h1>
        <p style={{ color: '#555', fontSize: 12 }}>Espace de contrôle documentaire</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 14, borderTop: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Référence · Capture du {format(lastUpdate, 'dd MMM. yyyy').toUpperCase()}
            </span>
          </div>
          <button onClick={fetchEvents} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #333', background: '#111', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Icon name="refresh" size={14} color="#fff" /> Actualiser
          </button>
        </div>
      </div>

      {/* Bloc info journal */}
      <div style={{ background: '#0d1117', border: '1px solid #1e1e1e', borderLeft: '3px solid #d4f535', borderRadius: 14, padding: 14, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Icon name="history" size={18} color="#d4f535" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>Journal des événements</div>
            <div style={{ fontSize: 11, color: '#555' }}>Accès et lecture · instantané de référence</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 48, fontWeight: 900, color: '#d4f535', lineHeight: 1 }}>{events.length}</span>
          <div>
            <div style={{ fontSize: 12, color: '#888' }}>événements annoncés</div>
            <div style={{ fontSize: 11, color: '#555' }}>{filtered.length} entrées visibles</div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={() => setFilterType('')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: filterType === '' ? '#d4f535' : 'transparent', color: filterType === '' ? '#0a0a0a' : '#555', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            <Icon name="history" size={14} color={filterType === '' ? '#0a0a0a' : '#555'} /> Tous
          </button>
          {Object.entries(EVT).map(([val, cfg]) => (
            <button key={val} onClick={() => setFilterType(val)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: filterType === val ? '#1a1a1a' : 'transparent', color: filterType === val ? cfg.color : '#555', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              <Icon name={cfg.icon} size={14} color={filterType === val ? cfg.color : '#555'} />
              {cfg.label}
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
            <Icon name="search" size={14} color="#555" />
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Membre, événement, heure..."
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 36px', borderRadius: 12, border: '1px solid #222', background: '#111', color: '#fff', fontSize: 13, outline: 'none' }} />
        </div>

        {/* Note info */}
        {events.length > filtered.length && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, padding: '10px 12px', background: '#0d0d0d', borderRadius: 10, border: '1px solid #1a1a1a' }}>
            <Icon name="info" size={13} color="#555" />
            <span style={{ fontSize: 11, color: '#555', lineHeight: 1.6 }}>
              {events.length - filtered.length} événements ne sont pas visibles dans la capture. Les filtres portent sur les {filtered.length} entrées reproduites uniquement.
            </span>
          </div>
        )}
      </div>

      {/* Liste groupée par date */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#555', fontSize: 13 }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#111', border: '1px solid #1e1e1e', borderRadius: 14 }}>
          <Icon name="file" size={32} color="#333" />
          <div style={{ color: '#555', marginTop: 10, fontSize: 13 }}>Aucun événement</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(grouped).map(([day, dayEvents]) => (
            <div key={day}>
              {/* Header date */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#ffffff' }}>{day}</span>
                <span style={{ fontSize: 10, color: '#444', fontWeight: 700, letterSpacing: 1 }}>
                  {dayEvents.length} / {dayEvents.length} VISIBLES
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dayEvents.map((e, i) => {
                  const conf = EVT[e.event_type] || EVT.open;
                  return (
                    <div key={e.id || i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: `3px solid ${conf.border}`, borderRadius: 14, padding: '14px 16px' }}>

                      {/* Ligne 1 : avatar + nom + type + heure */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#1a1a1a', border: `1px solid ${conf.border}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon name={conf.icon} size={16} color={conf.color} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>
                              {e.member_name || e.member_email?.split('@')[0] || 'Inconnu'}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: conf.color }}>
                              {conf.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>
                            {e.member_email || 'Contact masqué'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>
                            {format(new Date(e.created_at), 'dd/MM/yy')}
                          </div>
                          <div style={{ fontSize: 10, color: '#555', fontFamily: 'monospace' }}>
                            {format(new Date(e.created_at), 'HH:mm:ss')}
                          </div>
                        </div>
                      </div>

                      {/* Séparateur */}
                      <div style={{ borderTop: '1px solid #1a1a1a', marginBottom: 10 }} />

                      {/* Ligne 2 : tags techniques */}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                        {(e.city || e.country) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#888' }}>
                            <Icon name="globe" size={12} color="#555" />
                            <span>{[e.country, e.browser].filter(Boolean).join(' · ')}</span>
                          </div>
                        )}
                        {e.device_type && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#888' }}>
                            <Icon name={e.device_type === 'mobile' ? 'phone' : e.device_type === 'tablet' ? 'phone' : 'monitor'} size={12} color="#555" />
                            <span>{e.device_type.charAt(0).toUpperCase() + e.device_type.slice(1)}{e.os ? ' · ' + e.os : ''}</span>
                          </div>
                        )}
                        {e.ip ? (
                          <div style={{ fontSize: 11, color: '#4db8ff', fontFamily: 'monospace' }}>{e.ip}</div>
                        ) : (
                          <div style={{ fontSize: 11, color: '#444' }}>IP masquée</div>
                        )}
                        {e.access_email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#ef4444' }}>
                            <Icon name="mail" size={12} color="#ef4444" />
                            <span>{e.access_email}</span>
                          </div>
                        )}
                      </div>

                      {/* Ligne 3 : label entrée + Détails */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: '#444' }}>Entrée de référence</span>
                        <button onClick={() => setModal(e)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#d4f535', fontSize: 13, fontWeight: 800, cursor: 'pointer', padding: 0 }}>
                          Détails <Icon name="chevron" size={14} color="#d4f535" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Note bas */}
      <div style={{ borderTop: '1px solid #1a1a1a', marginTop: 20, paddingTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="shield" size={14} color="#333" />
        <div style={{ fontSize: 11, color: '#444', lineHeight: 1.6 }}>
          Contacts et IP masqués · actualisation manuelle de la référence, sans connexion à un journal réel.
        </div>
      </div>

      {/* ═══ MODAL DÉTAILS ═══ */}
      {modal && (() => {
        const conf = EVT[modal.event_type] || EVT.open;
        return (
          <div onClick={() => setModal(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#ffffff', marginBottom: 4 }}>
                    Détail · {modal.member_name || modal.member_email?.split('@')[0] || 'Inconnu'}
                  </div>
                  <div style={{ fontSize: 12, color: conf.color, fontWeight: 700 }}>{conf.label}</div>
                </div>
                <button onClick={() => setModal(null)}
                  style={{ background: '#1e1e1e', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginLeft: 12 }}>
                  <Icon name="x" size={16} color="#888" />
                </button>
              </div>

              <div style={{ borderTop: '1px solid #1e1e1e', marginBottom: 16 }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[
                  { label: 'Date',    value: format(new Date(modal.created_at), 'dd/MM/yyyy') },
                  { label: 'Heure',   value: format(new Date(modal.created_at), 'HH:mm:ss') },
                  { label: 'Contact', value: modal.member_email || 'Masqué' },
                  { label: 'Adresse IP', value: modal.ip || 'Masquée', mono: true, color: modal.ip ? '#4db8ff' : undefined },
                ].map((row, k) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{row.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: row.color || '#ffffff', fontFamily: row.mono ? 'monospace' : undefined }}>{row.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[
                  { label: 'Navigateur', value: modal.browser || '—' },
                  { label: 'Appareil',   value: modal.device_type ? modal.device_type.charAt(0).toUpperCase() + modal.device_type.slice(1) : '—' },
                  { label: 'OS',         value: modal.os || '—' },
                  { label: 'Pays',       value: [modal.city, modal.country].filter(Boolean).join(', ') || '—' },
                ].map((row, k) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{row.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>{row.value}</div>
                  </div>
                ))}
              </div>

              {modal.access_email && (
                <div style={{ background: '#1a0500', border: '1px solid #ef444433', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Email d'accès</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{modal.access_email}</div>
                </div>
              )}

              <div style={{ borderTop: '1px solid #1e1e1e', marginBottom: 16 }} />

              <div style={{ fontSize: 11, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
                Aucun identifiant de session n'est fourni. Cette entrée ne permet pas de tracer une action en cours.
              </div>

              <button onClick={() => setModal(null)}
                style={{ width: '100%', padding: '14px', borderRadius: 12, border: '1px solid #333', background: '#1a1a1a', color: '#ffffff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                Fermer les détails
              </button>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
